import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import {
  catchError,
  defer,
  EMPTY,
  filter,
  firstValueFrom,
  from,
  map,
  mergeMap,
  Observable,
  of,
  take,
  tap,
} from 'rxjs';
import {
  ConversationalBtmApiError,
  ConversationalBtmConversationDetail,
  ConversationalBtmRunMessage,
  mapConversationalBtmDetail,
  mapConversationalBtmRunState,
} from '../btm';
import {
  CopilotActiveRun,
  Message,
  MessageRole,
} from '../models/copilot.models';
import { CopilotApiService } from '../services/copilot.service';
import { CopilotActions } from './copilot.actions';
import {
  StructuredLogger,
  logCorrelationId,
  logFailureReason,
  telemetryInternalIds,
} from '@brighthub/logging';
import {
  selectClientGuidForConversation,
  selectedClientId,
} from './copilot.selectors';

@Injectable({ providedIn: 'root' })
export class CopilotEffects {
  private readonly logs = inject(StructuredLogger);
  private readonly actions$ = inject(Actions);
  private readonly api = inject(CopilotApiService);
  private readonly store = inject(Store);
  private readonly controllers = new Map<
    string,
    {
      clientId: string;
      conversationId: string;
      controller: AbortController;
    }
  >();
  private readonly currentLoadTokens = new Map<string, symbol>();

  loadMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CopilotActions.loadMessages),
      mergeMap(({ clientId, conversationId }) => {
        const key = runKey(clientId, conversationId);
        const token = Symbol();
        this.currentLoadTokens.set(key, token);
        return defer(() =>
          from(this.api.fetchConversation(clientId, conversationId)),
        ).pipe(
          filter(() => this.currentLoadTokens.get(key) === token),
          map((detail) => {
            this.abortRun(clientId, conversationId);
            const execution = detail.conversation.execution;
            const activeRun = activeRunFromDetail(detail);
            const resolvedAgentIdentifier = agentIdentifier(
              detail.conversation.metadata,
            );
            const success = CopilotActions.loadMessagesSuccess({
              clientId,
              conversationId,
              messages: mapConversationalBtmDetail(detail).messages,
              executionStatus: execution.status,
              ...(resolvedAgentIdentifier
                ? { agentIdentifier: resolvedAgentIdentifier }
                : {}),
              ...(activeRun ? { activeRun } : {}),
            });
            this.currentLoadTokens.delete(key);
            return success;
          }),
          catchError((error) => {
            if (this.currentLoadTokens.get(key) !== token) {
              return EMPTY;
            }
            this.currentLoadTokens.delete(key);
            this.api.reportLoadMessagesError(errorMessage(error));
            return of(
              CopilotActions.loadMessagesFailure({
                clientId,
                conversationId,
                error,
              }),
            );
          }),
        );
      }),
    ),
  );

  resumeAfterLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CopilotActions.loadMessagesSuccess),
      filter(
        (action): action is typeof action & { activeRun: CopilotActiveRun } =>
          action.activeRun !== undefined,
      ),
      map((action) =>
        CopilotActions.resumeRun({
          clientId: action.clientId,
          conversationId: action.conversationId,
          activeRun: action.activeRun,
          initialMessages: initialRunMessages(
            action.messages,
            action.activeRun.runId,
          ),
        }),
      ),
    ),
  );

  sendMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CopilotActions.sendMessage),
      mergeMap((action) => {
        this.currentLoadTokens.delete(
          runKey(action.clientId, action.conversationId),
        );
        return this.submitAndFollow(action);
      }),
    ),
  );

  resumeRun$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CopilotActions.resumeRun),
      mergeMap((action) =>
        this.followRun(
          action.clientId,
          action.conversationId,
          action.activeRun,
          action.initialMessages,
        ),
      ),
    ),
  );

  conversationSelected$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CopilotActions.selectConversation),
        tap(({ clientId, conversationId }) =>
          this.abortOtherRuns(clientId, conversationId),
        ),
      ),
    { dispatch: false },
  );

  clearClient$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CopilotActions.clearClient),
        tap(({ clientId }) => this.abortClientRuns(clientId)),
      ),
    { dispatch: false },
  );

  conversationInvalidated$ = createEffect(() =>
    this.api.invalidations$.pipe(
      map(({ clientId, conversationId }) =>
        CopilotActions.loadMessages({
          clientId,
          conversationId,
          forceRefresh: true,
        }),
      ),
    ),
  );

  assistantHighlight$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CopilotActions.runSnapshotReceived),
      filter((action) => action.messages.length > 0),
      mergeMap(({ clientId, conversationId }) =>
        this.store.select(selectedClientId).pipe(
          take(1),
          filter((selectedId) => selectedId !== clientId),
          map(() =>
            CopilotActions.triggerConversationHighlight({
              clientId,
              conversationId,
            }),
          ),
        ),
      ),
    ),
  );

  private submitAndFollow(action: {
    clientId: string;
    conversationId: string;
    content: string;
    optimisticUserId: string;
    startedAt: string;
    clientGuid?: string;
  }): Observable<Action> {
    return new Observable<Action>((subscriber) => {
      const interactionId = logCorrelationId();
      const submissionIds = telemetryInternalIds({
        client_id: action.clientId,
        conversation_id: action.conversationId,
        client_guid: action.clientGuid,
      });
      void (async () => {
        try {
          const clientGuid =
            action.clientGuid ??
            (await firstValueFrom(
              this.store
                .select(
                  selectClientGuidForConversation(
                    action.clientId,
                    action.conversationId,
                  ),
                )
                .pipe(take(1)),
            ));
          Object.assign(
            submissionIds,
            telemetryInternalIds({ client_guid: clientGuid }),
          );
          if (!clientGuid) {
            throw new Error(
              'The selected UI session is missing its client identifier.',
            );
          }

          const accepted = await this.api.submitMessage({
            clientId: action.clientId,
            clientGuid,
            conversationId: action.conversationId,
            content: action.content,
            idempotencyKey: uuid(),
          });
          this.logs.emit('copilot.submission.accepted', {
            ...submissionIds,
            run_id: accepted.run_id,
            feature: 'copilot',
            interaction_id: interactionId,
            stage: 'submission',
            outcome: 'accepted',
          });
          const activeRun: CopilotActiveRun = {
            runId: accepted.run_id,
            streamUrl: accepted.stream_url,
            status: 'connecting',
            attempt: 0,
            startedAt: action.startedAt,
          };
          this.currentLoadTokens.delete(
            runKey(action.clientId, action.conversationId),
          );
          subscriber.next(
            CopilotActions.submissionAccepted({
              clientId: action.clientId,
              conversationId: action.conversationId,
              activeRun,
            }),
          );
          this.api.broadcastInvalidation({
            clientId: action.clientId,
            conversationId: action.conversationId,
          });
          await this.followRunInto(
            subscriber,
            action.clientId,
            action.conversationId,
            activeRun,
            [],
            interactionId,
            clientGuid,
          );
        } catch (error) {
          if (!isAbort(error)) {
            this.logs.emit('copilot.run.failed', {
              ...submissionIds,
              feature: 'copilot',
              interaction_id: interactionId,
              stage: 'submission',
              outcome: 'failed',
              ...logFailureReason(error),
            });
            const message = errorMessage(error);
            subscriber.next(
              CopilotActions.runFailed({
                clientId: action.clientId,
                conversationId: action.conversationId,
                optimisticUserId: action.optimisticUserId,
                error: message,
              }),
            );
            if (
              error instanceof ConversationalBtmApiError &&
              error.status === 409
            ) {
              subscriber.next(
                CopilotActions.loadMessages({
                  clientId: action.clientId,
                  conversationId: action.conversationId,
                  forceRefresh: true,
                }),
              );
            }
            this.api.reportRunError(message);
          }
        } finally {
          subscriber.complete();
        }
      })();
    });
  }

  private followRun(
    clientId: string,
    conversationId: string,
    activeRun: CopilotActiveRun,
    initialMessages: ConversationalBtmRunMessage[],
  ): Observable<Action> {
    return new Observable<Action>((subscriber) => {
      void this.followRunInto(
        subscriber,
        clientId,
        conversationId,
        activeRun,
        initialMessages,
      ).finally(() => subscriber.complete());
    });
  }

  private async followRunInto(
    subscriber: { next(action: Action): void },
    clientId: string,
    conversationId: string,
    activeRun: CopilotActiveRun,
    initialMessages: ConversationalBtmRunMessage[],
    interactionId = logCorrelationId(),
    clientGuid?: string,
  ): Promise<void> {
    this.abortRun(clientId, conversationId);
    const key = runKey(clientId, conversationId);
    const controller = new AbortController();
    this.controllers.set(key, { clientId, conversationId, controller });
    const started = Date.now();
    let lastReconnectAttempt = -1;
    let reconnects = 0;
    const context = {
      ...telemetryInternalIds({
        client_id: clientId,
        conversation_id: conversationId,
        client_guid: clientGuid,
        run_id: activeRun.runId,
      }),
      feature: 'copilot',
      interaction_id: interactionId,
      stage: 'execution',
    };

    try {
      const result = await this.api.followRun({
        conversationId,
        runId: activeRun.runId,
        streamUrl: activeRun.streamUrl,
        signal: controller.signal,
        initialMessages,
        initialLastEventId: activeRun.lastEventId,
        onConnectionChange: (status, attempt) => {
          if (this.controllers.get(key)?.controller !== controller) return;
          if (status === 'reconnecting' && attempt !== lastReconnectAttempt) {
            lastReconnectAttempt = attempt;
            reconnects++;
            this.logs.emit('copilot.run.reconnecting', {
              ...context,
              outcome: 'reconnecting',
              attempt_count: attempt,
            });
          }
          subscriber.next(
            CopilotActions.runConnectionChanged({
              clientId,
              conversationId,
              status,
              attempt,
            }),
          );
        },
        onSnapshot: (snapshot) => {
          if (this.controllers.get(key)?.controller !== controller) return;
          const mapped = mapConversationalBtmRunState(
            snapshot,
            conversationId,
            activeRun.startedAt,
          );
          subscriber.next(
            CopilotActions.runSnapshotReceived({
              clientId,
              conversationId,
              runId: snapshot.runId,
              messages: mapped
                ? [
                    {
                      ...mapped,
                      metadata: {
                        ...mapped.metadata,
                        finalized: false,
                      },
                    },
                  ]
                : [],
              lastEventId: snapshot.lastEventId,
              error: snapshot.error,
            }),
          );
        },
      });
      if (this.controllers.get(key)?.controller !== controller) return;
      const detail = result.detail;
      if (result.state.error) {
        this.logs.emit('copilot.run.failed', {
          ...context,
          outcome: 'failed',
          reason_code: 'unknown',
          duration_ms: Date.now() - started,
        });
      } else if (reconnects > 0) {
        this.logs.emit('copilot.run.recovered', {
          ...context,
          outcome: 'recovered',
          attempt_count: reconnects,
          duration_ms: Date.now() - started,
        });
      }
      subscriber.next(
        CopilotActions.runReconciled({
          clientId,
          conversationId,
          messages: mapConversationalBtmDetail(detail).messages,
          executionStatus: detail.conversation.execution.status,
          error: result.state.error,
        }),
      );
      if (result.state.error) this.api.reportRunError(result.state.error);
      this.api.broadcastInvalidation({ clientId, conversationId });
    } catch (error) {
      if (
        this.controllers.get(key)?.controller !== controller ||
        isAbort(error)
      ) {
        return;
      }
      const message = errorMessage(error);
      this.logs.emit('copilot.run.failed', {
        ...context,
        outcome: 'failed',
        ...logFailureReason(error),
        duration_ms: Date.now() - started,
      });
      if (
        error instanceof ConversationalBtmApiError &&
        error.code === 'RUN_DISCONNECTED'
      ) {
        subscriber.next(
          CopilotActions.runDisconnected({
            clientId,
            conversationId,
            error: message,
          }),
        );
      } else {
        subscriber.next(
          CopilotActions.runFailed({
            clientId,
            conversationId,
            error: message,
          }),
        );
      }
      this.api.reportRunError(message);
    } finally {
      if (this.controllers.get(key)?.controller === controller) {
        this.controllers.delete(key);
      }
    }
  }

  private abortRun(clientId: string, conversationId: string): void {
    const key = runKey(clientId, conversationId);
    this.controllers.get(key)?.controller.abort();
    this.controllers.delete(key);
  }

  private abortOtherRuns(clientId: string, conversationId: string): void {
    for (const [key, active] of this.controllers) {
      if (
        active.clientId === clientId &&
        active.conversationId !== conversationId
      ) {
        active.controller.abort();
        this.controllers.delete(key);
      }
    }
  }

  private abortClientRuns(clientId: string): void {
    for (const [key, active] of this.controllers) {
      if (active.clientId === clientId) {
        active.controller.abort();
        this.controllers.delete(key);
      }
    }
  }
}

function activeRunFromDetail(
  detail: ConversationalBtmConversationDetail,
): CopilotActiveRun | undefined {
  const execution = detail.conversation.execution;
  if (
    (execution.status !== 'queued' && execution.status !== 'running') ||
    !execution.run_id ||
    !execution.stream_url
  ) {
    return undefined;
  }
  return {
    runId: execution.run_id,
    streamUrl: execution.stream_url,
    status: 'connecting',
    attempt: 0,
    startedAt: new Date().toISOString(),
  };
}

function initialRunMessages(
  messages: Message[],
  runId: string,
): ConversationalBtmRunMessage[] {
  return messages.flatMap((message) => {
    if (
      message.role !== MessageRole.ASSISTANT ||
      message.metadata?.['run_id'] !== runId
    ) {
      return [];
    }
    const parts = Array.isArray(message.metadata?.['parts'])
      ? message.metadata?.['parts']
      : [];
    return [
      {
        id: message.id,
        content: message.content,
        parts: parts as ConversationalBtmRunMessage['parts'],
      },
    ];
  });
}

function runKey(clientId: string, conversationId: string): string {
  return JSON.stringify([clientId, conversationId]);
}

function uuid(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
}

function errorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : 'Something went wrong loading the conversation.';
}

function agentIdentifier(
  metadata: Record<string, unknown>,
): string | undefined {
  const value = metadata['agent_identifier'];
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function isAbort(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { name?: unknown }).name === 'AbortError'
  );
}
